<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-emerald-600"></div>
    </div>
    
    <div v-else-if="!prestamo" class="text-center py-16">
      <Icon icon="mdi:alert-circle" class="text-6xl text-gray-300 mx-auto mb-4" />
      <h3 class="text-xl font-black text-gray-500">Préstamo no encontrado</h3>
      <router-link to="/prestamos" class="mt-4 inline-block px-6 py-2 bg-emerald-600 text-white rounded-xl font-black text-sm hover:bg-emerald-700 transition-all">
        Volver a la lista
      </router-link>
    </div>

    <div v-else>
      <!-- Header -->
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
        <div class="animate-fadeIn w-full sm:w-auto">
          <div class="flex items-center gap-2 mb-1">
            <div class="w-8 h-1 bg-emerald-600 rounded-full"></div>
            <span class="text-[10px] sm:text-xs font-black text-emerald-600 tracking-widest">Detalle</span>
          </div>
          <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-3">
            <Icon icon="mdi:receipt-text" class="text-emerald-600" />
            {{ prestamo.numeroPrestamo }}
            <span class="px-3 py-1 text-xs font-black rounded-lg" :class="getEstadoClass(prestamo.estado)">
              {{ formatearEstadoPrestamo(prestamo.estado) }}
            </span>
          </h1>
        </div>
        <div class="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <router-link to="/prestamos" class="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-xs hover:bg-gray-200 transition-all flex items-center gap-2">
            <Icon icon="mdi:arrow-left" /> Volver
          </router-link>
          <button v-if="prestamo.estado !== 'pagado' && prestamo.estado !== 'cancelado'" @click="abrirModalPago" class="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-xs transition-all flex items-center gap-2">
            <Icon icon="mdi:cash" /> Registrar Pago
          </button>
        </div>
      </div>

      <!-- Resumen -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 animate-fadeInUp delay-100">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">Prestatario</p>
          <p class="text-base font-black text-gray-900 dark:text-white">{{ prestamo.nombrePrestatario || prestamo.nombreUsuario || prestamo.nombreCliente || 'N/A' }}</p>
          <p class="text-xs text-gray-500">{{ prestamo.cedulaPrestatario || prestamo.documento || '' }}</p>
        </div>
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">Tipo</p>
          <p class="text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
            <Icon :icon="getTipoIcon(prestamo.tipoPrestatario)" />
            {{ formatearTipoPrestatario(prestamo.tipoPrestatario) }}
          </p>
        </div>
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">Monto Total</p>
          <p class="text-base font-black text-gray-900 dark:text-white">{{ formatCurrency(prestamo.totalDeudaUsd) }}</p>
          <p class="text-xs text-gray-500">{{ formatCurrencyBs(prestamo.totalDeudaBs) }}</p>
        </div>
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">Saldo Pendiente</p>
          <p class="text-base font-black text-emerald-600">{{ formatCurrency(prestamo.saldoPendienteUsd) }}</p>
          <p class="text-xs text-gray-500">{{ formatCurrencyBs(prestamo.saldoPendienteBs) }}</p>
        </div>
      </div>

      <!-- Detalles -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div class="lg:col-span-2 space-y-4">
          <!-- Información -->
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Información del Préstamo</h3>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p class="text-[9px] font-black text-gray-400">Fecha de Préstamo</p>
                <p class="font-bold text-gray-900 dark:text-white">{{ formatDate(prestamo.fechaPrestamo) }}</p>
              </div>
              <div>
                <p class="text-[9px] font-black text-gray-400">Fecha de Vencimiento</p>
                <p class="font-bold" :class="getVencimientoClass(prestamo.fechaVencimiento)">{{ formatDate(prestamo.fechaVencimiento) }}</p>
              </div>
              <div>
                <p class="text-[9px] font-black text-gray-400">Interés</p>
                <p class="font-bold text-gray-900 dark:text-white">{{ prestamo.interesPorcentaje || 0 }}%</p>
              </div>
              <div>
                <p class="text-[9px] font-black text-gray-400">Cuotas</p>
                <p class="font-bold text-gray-900 dark:text-white">{{ prestamo.numeroCuotas || 1 }}</p>
              </div>
              <div>
                <p class="text-[9px] font-black text-gray-400">Método de Pago</p>
                <p class="font-bold text-gray-900 dark:text-white">{{ formatearMetodoPago(prestamo.metodoPago) }}</p>
              </div>
              <div>
                <p class="text-[9px] font-black text-gray-400">Referencia</p>
                <p class="font-bold text-gray-900 dark:text-white">{{ prestamo.referenciaPago || 'N/A' }}</p>
              </div>
            </div>
            <div v-if="prestamo.motivo || prestamo.observaciones" class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <p v-if="prestamo.motivo" class="text-sm"><span class="font-black text-gray-500">Motivo:</span> {{ prestamo.motivo }}</p>
              <p v-if="prestamo.observaciones" class="text-sm"><span class="font-black text-gray-500">Observaciones:</span> {{ prestamo.observaciones }}</p>
            </div>
          </div>

          <!-- Cuotas -->
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Cuotas ({{ prestamo.cuotas?.length || 0 }})</h3>
            <div v-if="!prestamo.cuotas?.length" class="text-center py-4 text-gray-500 text-sm">
              No hay cuotas registradas
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-left text-xs">
                <thead class="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th class="px-3 py-2 font-black text-gray-400 uppercase">N°</th>
                    <th class="px-3 py-2 font-black text-gray-400 uppercase">Monto</th>
                    <th class="px-3 py-2 font-black text-gray-400 uppercase">Saldo</th>
                    <th class="px-3 py-2 font-black text-gray-400 uppercase">Vencimiento</th>
                    <th class="px-3 py-2 font-black text-gray-400 uppercase text-center">Estado</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
                  <tr v-for="cuota in prestamo.cuotas" :key="cuota.idCuota">
                    <td class="px-3 py-2 font-bold">#{{ cuota.numeroCuota }}</td>
                    <td class="px-3 py-2">{{ formatCurrency(cuota.montoUsd) }}</td>
                    <td class="px-3 py-2 font-bold text-emerald-600">{{ formatCurrency(cuota.saldoUsd) }}</td>
                    <td class="px-3 py-2">{{ formatDate(cuota.fechaVencimiento) }}</td>
                    <td class="px-3 py-2 text-center">
                      <span class="px-2 py-0.5 rounded-lg text-[8px] font-black uppercase" :class="getEstadoCuotaClass(cuota.estado)">
                        {{ cuota.estado }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Abonos -->
        <div class="space-y-4">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Historial de Pagos</h3>
            <div v-if="!prestamo.abonos?.length" class="text-center py-4 text-gray-500 text-sm">
              No hay pagos registrados
            </div>
            <div v-else class="space-y-2 max-h-96 overflow-y-auto">
              <div v-for="abono in prestamo.abonos" :key="abono.idAbono" class="border-b border-gray-100 dark:border-gray-700 pb-2 last:border-0">
                <div class="flex justify-between items-start">
                  <div>
                    <p class="font-bold text-emerald-600">{{ formatCurrency(abono.montoUsd) }}</p>
                    <p class="text-[10px] text-gray-500">{{ formatDate(abono.fechaAbono) }}</p>
                  </div>
                  <span class="text-[8px] font-black uppercase px-2 py-0.5 rounded-lg bg-gray-100 text-gray-600">{{ abono.tipoAbono }}</span>
                </div>
                <p class="text-[10px] text-gray-400">{{ abono.metodoPago }} {{ abono.referencia ? '- ' + abono.referencia : '' }}</p>
                <p class="text-[10px] text-gray-400">Registrado por: {{ abono.nombreUsuarioRegistro }}</p>
              </div>
            </div>
          </div>

          <!-- Resumen Rápido -->
          <div class="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl shadow-lg p-4 text-white">
            <p class="text-[9px] font-black opacity-70 uppercase tracking-widest">Resumen</p>
            <div class="grid grid-cols-2 gap-2 mt-2">
              <div>
                <p class="text-[9px] opacity-70">Pagado</p>
                <p class="text-lg font-black">{{ formatCurrency(prestamo.totalDeudaUsd - prestamo.saldoPendienteUsd) }}</p>
              </div>
              <div>
                <p class="text-[9px] opacity-70">Pendiente</p>
                <p class="text-lg font-black">{{ formatCurrency(prestamo.saldoPendienteUsd) }}</p>
              </div>
              <div>
                <p class="text-[9px] opacity-70">Cuotas Pagadas</p>
                <p class="text-lg font-black">{{ prestamo.cuotasPagadas || 0 }}</p>
              </div>
              <div>
                <p class="text-[9px] opacity-70">Pendientes</p>
                <p class="text-lg font-black">{{ prestamo.cuotasPendientes || 0 }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Pago (reutilizado de PrestamosList) -->
    <Teleport to="body">
      <div v-if="showPagoModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="showPagoModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 sm:px-6 py-4 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:cash" class="text-white text-xl" />
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-black text-white">Registrar Pago</h3>
              <p class="text-[10px] text-white/70 font-bold">{{ prestamo?.numeroPrestamo || '' }}</p>
            </div>
            <button @click="showPagoModal = false" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-6">
            <form @submit.prevent="registrarPago">
              <!-- Mismo formulario de pago que en PrestamosList -->
              <div class="space-y-4">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-3">
                  <div class="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p class="text-[10px] font-black text-gray-400">Total</p>
                      <p class="font-black text-gray-900 dark:text-white">{{ formatCurrency(prestamo?.totalDeudaUsd || 0) }}</p>
                    </div>
                    <div>
                      <p class="text-[10px] font-black text-gray-400">Saldo</p>
                      <p class="font-black text-emerald-600">{{ formatCurrency(prestamo?.saldoPendienteUsd || 0) }}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Monto a Pagar (USD)</label>
                  <input v-model="pagoForm.montoUsd" type="number" step="0.01" min="0" class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-bold outline-none" placeholder="0.00" />
                </div>

                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Monto a Pagar (Bs)</label>
                  <input v-model="pagoForm.montoBs" type="number" step="0.01" min="0" class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-bold outline-none" placeholder="0.00" />
                </div>

                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Tasa de Cambio (Bs/USD) *</label>
                  <input v-model="pagoForm.tasaBsUsd" type="number" step="0.01" min="0" required class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-bold outline-none" placeholder="Ej: 40.00" />
                </div>

                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Método de Pago</label>
                  <select v-model="pagoForm.metodoPago" class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-bold outline-none appearance-none cursor-pointer">
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="pago_movil">Pago Móvil</option>
                    <option value="zelle">Zelle</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>

                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Referencia</label>
                  <input v-model="pagoForm.referencia" type="text" class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-medium outline-none" placeholder="Número de referencia" />
                </div>

                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Observaciones</label>
                  <textarea v-model="pagoForm.observaciones" rows="2" class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-medium outline-none resize-none" placeholder="Observaciones..."></textarea>
                </div>
              </div>

              <div class="flex flex-col sm:flex-row gap-3 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="showPagoModal = false" class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest hover:bg-gray-50 transition-all">Cancelar</button>
                <button type="submit" :disabled="registrandoPago" class="w-full sm:flex-[2] py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-emerald-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="registrandoPago ? 'mdi:loading' : 'mdi:cash'" :class="{'animate-spin': registrandoPago}" />
                  {{ registrandoPago ? 'Procesando...' : 'Registrar Pago' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { obtenerPrestamoDetalle, registrarPagoRapido } from '@/api/prestamos.js';
import { getCurrentUser } from '@/utils/permissionUtils';

export default {
  name: 'PrestamoDetail',
  components: { Icon },
  data() {
    return {
      loading: true,
      prestamo: null,
      showPagoModal: false,
      registrandoPago: false,
      pagoForm: {
        idPrestamo: null,
        montoUsd: 0,
        montoBs: 0,
        tasaBsUsd: 0,
        metodoPago: 'efectivo',
        referencia: '',
        observaciones: '',
        idUsuarioRegistro: null
      }
    };
  },
  mounted() {
    const id = this.$route.params.id;
    if (id) this.loadPrestamo(id);
  },
  methods: {
    async loadPrestamo(id) {
      this.loading = true;
      try {
        const result = await obtenerPrestamoDetalle(id);
        if (result.success) {
          this.prestamo = result.data;
          this.pagoForm.idPrestamo = this.prestamo.idPrestamo;
          this.pagoForm.idUsuarioRegistro = getCurrentUser()?.idUsuario || 1;
        }
      } catch (error) {
        console.error('Error cargando préstamo:', error);
      } finally {
        this.loading = false;
      }
    },
    abrirModalPago() {
      this.pagoForm.montoUsd = this.prestamo.saldoPendienteUsd || 0;
      this.pagoForm.tasaBsUsd = 0;
      this.showPagoModal = true;
    },
    async registrarPago() {
      if (!this.pagoForm.montoUsd && !this.pagoForm.montoBs) {
        alert('Debe ingresar al menos un monto');
        return;
      }
      if (!this.pagoForm.tasaBsUsd) {
        alert('La tasa de cambio es requerida');
        return;
      }

      this.registrandoPago = true;
      try {
        const result = await registrarPagoRapido(this.pagoForm);
        if (result.success) {
          this.showPagoModal = false;
          await this.loadPrestamo(this.prestamo.idPrestamo);
          alert('Pago registrado exitosamente');
        } else {
          alert(result.error || 'Error al registrar el pago');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error al registrar el pago');
      } finally {
        this.registrandoPago = false;
      }
    },
    // Utilidades (mismas que en PrestamosList)
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
    },
    formatCurrencyBs(value) {
      return new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'VES' }).format(value || 0);
    },
    formatDate(fecha) {
      if (!fecha) return 'N/A';
      try {
        return new Date(fecha).toLocaleDateString('es-VE', { year: 'numeric', month: '2-digit', day: '2-digit' });
      } catch {
        return fecha;
      }
    },
    formatearEstadoPrestamo(estado) {
      const map = {
        'activo': 'Activo',
        'pagado': 'Pagado',
        'vencido': 'Vencido',
        'parcial': 'Parcial',
        'cancelado': 'Cancelado'
      };
      return map[estado?.toLowerCase()] || estado || 'Desconocido';
    },
    formatearTipoPrestatario(tipo) {
      const map = {
        'empleado': 'Empleado',
        'cliente': 'Cliente',
        'externo': 'Externo'
      };
      return map[tipo?.toLowerCase()] || tipo || 'Desconocido';
    },
    formatearMetodoPago(metodo) {
      const map = {
        'efectivo': 'Efectivo',
        'transferencia': 'Transferencia',
        'pago_movil': 'Pago Móvil',
        'zelle': 'Zelle',
        'cheque': 'Cheque'
      };
      return map[metodo?.toLowerCase()] || metodo || 'Desconocido';
    },
    getEstadoClass(estado) {
      const map = {
        'activo': 'bg-blue-100 text-blue-700 border border-blue-200',
        'pagado': 'bg-emerald-100 text-emerald-700 border border-emerald-200',
        'vencido': 'bg-rose-100 text-rose-700 border border-rose-200',
        'parcial': 'bg-amber-100 text-amber-700 border border-amber-200',
        'cancelado': 'bg-gray-100 text-gray-600 border border-gray-200'
      };
      return map[estado?.toLowerCase()] || map.activo;
    },
    getEstadoCuotaClass(estado) {
      const map = {
        'pendiente': 'bg-amber-100 text-amber-700',
        'pagada': 'bg-emerald-100 text-emerald-700',
        'vencida': 'bg-rose-100 text-rose-700',
        'parcial': 'bg-blue-100 text-blue-700'
      };
      return map[estado?.toLowerCase()] || 'bg-gray-100 text-gray-600';
    },
    getTipoIcon(tipo) {
      const map = {
        'empleado': 'mdi:account-tie',
        'cliente': 'mdi:account',
        'externo': 'mdi:account-outline'
      };
      return map[tipo?.toLowerCase()] || 'mdi:account';
    },
    getVencimientoClass(fecha) {
      if (!fecha) return 'text-gray-400';
      const dias = this.calcularDias(fecha);
      if (dias < 0) return 'text-rose-600';
      if (dias <= 3) return 'text-amber-600';
      return 'text-gray-900 dark:text-white';
    },
    calcularDias(fecha) {
      if (!fecha) return null;
      const hoy = new Date();
      const vencimiento = new Date(fecha);
      return Math.ceil((vencimiento - hoy) / (1000 * 60 * 60 * 24));
    }
  }
};
</script>